import { useEffect, useState } from "react";
import { Mail, Shield, CheckCircle2, ExternalLink, Loader2, AlertTriangle, PlugZap, PowerOff } from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import AppShell, { getActiveSchemeId } from "@/components/AppShell";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

// Sprint P2-S1 — Gmail connect wizard.
//
// Non-negotiable privacy line (v2.1 brief, verbatim): "OPSTRAIT will only
// access emails you have labelled as Strata. Your other emails are never
// accessed." — this string is shown in Step 1 and again on Step 3.

const STEPS = ["Privacy", "Consent", "Auto-label", "Done"];

export default function GmailConnect() {
  const [step, setStep] = useState(0);
  const [status, setStatus] = useState(null);
  const [authUrl, setAuthUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [gate503, setGate503] = useState(null);
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const activeSid = sp.get("scheme_id") || getActiveSchemeId();

  const loadStatus = async () => {
    if (!activeSid) return;
    try {
      const { data } = await api.get(`/connectors/gmail/status?scheme_id=${activeSid}`);
      setStatus(data);
      if (data.status === "CONNECTED") setStep(3);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 503 && detail?.error === "gmail_credentials_missing") {
        setGate503(detail);
      } else {
        toast.error(apiError(e));
      }
    }
  };
  useEffect(() => { loadStatus(); }, [activeSid]);

  const startConsent = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/connectors/gmail/authorize?scheme_id=${activeSid}`);
      setAuthUrl(data.authorize_url);
      // Open Google consent in a popup so we can poll for CONNECTED state.
      const w = window.open(data.authorize_url, "gmail-consent", "width=520,height=720");
      if (!w) toast.error("Popup blocked — please allow popups and try again.");
      // Poll status every 3s for up to 3 minutes.
      let ticks = 0;
      const iv = setInterval(async () => {
        ticks += 1;
        try {
          const { data: s } = await api.get(`/connectors/gmail/status?scheme_id=${activeSid}`);
          if (s.status === "CONNECTED") {
            clearInterval(iv);
            setStatus(s);
            setStep(2);
            toast.success("Gmail connected. The OPSTRAIT Strata label is now in your inbox.");
          }
        } catch { /* silent */ }
        if (ticks >= 60) clearInterval(iv);
      }, 3000);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 503 && detail?.error === "gmail_credentials_missing") {
        setGate503(detail);
      } else {
        toast.error(apiError(e));
      }
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.delete(`/connectors/gmail/disconnect?scheme_id=${activeSid}`);
      toast.success("Gmail disconnected.");
      setStatus({ status: "DISCONNECTED" });
      setStep(0);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto px-5 lg:px-8 py-8" data-testid="gmail-connect-page">
        <div className="flex items-center gap-2 mb-2">
          <Mail size={22} style={{ color: "var(--blue)" }} />
          <h1 className="text-2xl font-bold" style={{ color: "var(--navy)" }} data-testid="gmail-connect-heading">Connect Gmail</h1>
        </div>
        <p className="text-sm mb-6" style={{ color: "var(--slate)" }}>
          OPSTRAIT reads only emails you tag with the <code>OPSTRAIT Strata</code> label
          in your Gmail. Every other email in your inbox stays private.
        </p>

        {/* Step indicator */}
        <ol className="flex items-center gap-2 mb-6 text-[11px] font-medium uppercase tracking-wider" data-testid="gmail-step-indicator">
          {STEPS.map((s, i) => (
            <li
              key={s}
              className="flex items-center gap-2"
              style={{ color: i <= step ? "var(--navy)" : "var(--slate)" }}
              data-testid={`step-${i}`}
            >
              <span
                className="flex items-center justify-center rounded-full border w-6 h-6 text-[11px]"
                style={{
                  background: i < step ? "var(--navy)" : "white",
                  color: i < step ? "white" : (i === step ? "var(--navy)" : "var(--slate)"),
                  borderColor: i <= step ? "var(--navy)" : "var(--border)",
                }}
              >
                {i < step ? <CheckCircle2 size={12} /> : i + 1}
              </span>
              {s}
              {i < STEPS.length - 1 && <span className="mx-1" style={{ color: "var(--slate)" }}>›</span>}
            </li>
          ))}
        </ol>

        {gate503 && <ConfigMissingCard detail={gate503} />}

        {!gate503 && step === 0 && (
          <StepCard testid="step-privacy">
            <h2 className="text-lg font-semibold" style={{ color: "var(--navy)" }}>How the privacy filter works</h2>
            <p className="text-sm mt-2" style={{ color: "var(--slate)" }}>
              OPSTRAIT will only read emails you have labelled as <strong>OPSTRAIT Strata</strong>
              in your Gmail. Your other emails are never accessed.
            </p>
            <ul className="mt-3 space-y-2 text-sm" style={{ color: "var(--slate)" }}>
              <li>• We request only two Gmail scopes — read+reply on labelled emails, and create the label itself.</li>
              <li>• Google never grants us access to your other emails.</li>
              <li>• You can disconnect at any time — this revokes our access instantly with Google.</li>
            </ul>
            <Button onClick={() => setStep(1)} data-testid="privacy-next" className="mt-5" style={{ background: "var(--blue)", color: "white" }}>
              I understand — continue
            </Button>
          </StepCard>
        )}

        {!gate503 && step === 1 && (
          <StepCard testid="step-consent">
            <h2 className="text-lg font-semibold" style={{ color: "var(--navy)" }}>Connect Gmail</h2>
            <p className="text-sm mt-2" style={{ color: "var(--slate)" }}>
              You'll be redirected to Google to grant access. The consent screen
              will list the two scopes shown below.
            </p>
            <div className="mt-3 rounded-md border p-3 text-xs font-mono" style={{ borderColor: "var(--border)", color: "var(--navy)" }}>
              gmail.modify · gmail.labels
            </div>
            <Button onClick={startConsent} disabled={busy || !activeSid} data-testid="start-consent-btn" className="mt-5" style={{ background: "var(--blue)", color: "white" }}>
              {busy ? <><Loader2 size={14} className="mr-2 animate-spin"/>Redirecting…</> : <><PlugZap size={14} className="mr-2"/>Connect Gmail</>}
            </Button>
            {authUrl && (
              <div className="mt-3 text-xs" style={{ color: "var(--slate)" }}>
                Popup blocked? <a href={authUrl} target="_blank" rel="noreferrer" style={{ color: "var(--blue)" }}>Open the Google consent screen manually <ExternalLink size={11} className="inline"/></a>
              </div>
            )}
          </StepCard>
        )}

        {!gate503 && step === 2 && (
          <StepCard testid="step-autolabel">
            <h2 className="text-lg font-semibold" style={{ color: "var(--navy)" }}>Set up an auto-label filter</h2>
            <p className="text-sm mt-2" style={{ color: "var(--slate)" }}>
              The label <code>OPSTRAIT Strata</code> now exists in your Gmail. For emails from
              your lot owners to appear here automatically, set up a Gmail filter:
            </p>
            <ol className="mt-3 space-y-2 text-sm list-decimal pl-5" style={{ color: "var(--slate)" }}>
              <li>Open Gmail → click the gear icon → <strong>See all settings</strong>.</li>
              <li>Go to <strong>Filters and Blocked Addresses</strong> → <strong>Create a new filter</strong>.</li>
              <li>In <strong>From</strong>, enter a comma-separated list of lot owner email domains (or their exact addresses).</li>
              <li>Click <strong>Create filter</strong>.</li>
              <li>Tick <strong>Apply the label</strong> → choose <strong>OPSTRAIT Strata</strong>.</li>
              <li>(Optional) Tick <strong>Also apply filter to matching conversations</strong> to back-label historical emails.</li>
            </ol>
            <p className="text-xs mt-3" style={{ color: "var(--slate)" }}>
              Google's API doesn't reliably support programmatic filter creation, so this step is manual — but it's a one-time setup.
            </p>
            <Button onClick={() => setStep(3)} className="mt-5" style={{ background: "var(--blue)", color: "white" }} data-testid="autolabel-done-btn">
              I've set up the filter
            </Button>
          </StepCard>
        )}

        {!gate503 && step === 3 && (
          <StepCard testid="step-done">
            <div className="flex items-center gap-2">
              <CheckCircle2 size={22} style={{ color: "#166534" }}/>
              <h2 className="text-lg font-semibold" style={{ color: "var(--navy)" }}>Gmail connected</h2>
            </div>
            {status && (
              <div className="mt-3 rounded-md border p-3 text-xs" style={{ borderColor: "var(--border)", color: "var(--slate)" }}>
                <div>Status: <strong style={{ color: "#166534" }}>{status.status || "CONNECTED"}</strong></div>
                <div>Label: <code>{status.label_name || "OPSTRAIT Strata"}</code></div>
                <div>Last synced: {status.last_synced_at || "not yet — first sync runs within 5 minutes"}</div>
                {status.error_message && <div style={{ color: "#991b1b" }}>Error: {status.error_message}</div>}
              </div>
            )}
            <div className="flex gap-2 mt-5">
              <Button onClick={() => navigate("/scheme/messaging/email")} style={{ background: "var(--blue)", color: "white" }} data-testid="go-inbox-btn">
                Open the Email inbox
              </Button>
              <Button variant="outline" onClick={disconnect} disabled={busy} data-testid="disconnect-btn">
                <PowerOff size={14} className="mr-1.5"/> Disconnect
              </Button>
            </div>
          </StepCard>
        )}
      </div>
    </AppShell>
  );
}

function StepCard({ children, testid }) {
  return (
    <div className="rounded-xl bg-white border p-5" style={{ borderColor: "var(--border)" }} data-testid={testid}>
      {children}
    </div>
  );
}

function ConfigMissingCard({ detail }) {
  return (
    <div
      className="rounded-md border-l-4 p-4 text-sm flex gap-3"
      style={{ background: "#fef3c7", borderLeftColor: "#f59e0b", color: "#78350f" }}
      data-testid="gmail-config-missing-card"
    >
      <AlertTriangle size={16} className="shrink-0 mt-0.5" />
      <div>
        <div className="font-semibold" style={{ color: "#78350f" }}>Gmail sandbox not configured yet</div>
        <div className="mt-1 text-xs">
          The platform admin needs to register OPSTRAIT with Google Cloud and paste
          the client credentials into <code>backend/.env</code>. Missing env vars:
          <code className="ml-1">{(detail?.missing || []).join(", ")}</code>.
        </div>
        <div className="mt-1 text-xs">See the setup checklist in the PRD.</div>
      </div>
    </div>
  );
}
